import React, { useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '~/utils/classnames'
import { composeRefs } from '~/utils/react'

const TextEditableRoot = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<'div'>) => {
  return (
    <div className={cn('relative', className)} {...props}>
      <div
        className={cn(
          'border-accent-element/20 bg-accent-element/20 pointer-events-none absolute inset-0 rounded-sm border',
          '[animation:1s_0.75s_linear_text-editable-swoosh] [mask-image:linear-gradient(45deg,transparent_var(--text-editable-mask-from),black_var(--text-editable-mask-via),transparent_var(--text-editable-mask-to))]'
        )}
      />
      {children}
    </div>
  )
}

const TextEditableTextarea = ({
  ref,
  className,
  preventLineBreaks = false,
  invalid,
  ...props
}: React.ComponentPropsWithRef<'textarea'> & {
  preventLineBreaks?: boolean
  invalid?: boolean
}) => {
  const internalRef = useRef<HTMLTextAreaElement>(null)
  const [internalValue, setInternalValue] = useState(props.value)

  const value = props.value ?? internalValue

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (preventLineBreaks) {
      event.target.value = event.target.value.replace(/\n/g, '')
    }

    setInternalValue(event.target.value)
    props.onChange?.(event)
  }

  const adjustHeight = useCallback(() => {
    if (internalRef.current) {
      internalRef.current.style.height = 'auto'
      internalRef.current.style.height = `${internalRef.current.scrollHeight}px`
    }
  }, [])

  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  useEffect(() => {
    const element = internalRef.current
    if (!element) return

    const resizeObserver = new ResizeObserver(() => {
      adjustHeight()
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [adjustHeight])

  return (
    <textarea
      ref={composeRefs(ref, internalRef)}
      data-invalid={invalid}
      aria-invalid={invalid}
      className={cn(
        'hover:bg-foreground/5 focus-visible:bg-accent-element/12 block h-auto w-full resize-none rounded-sm text-current ring-0! transition',
        'data-[invalid=true]:animate-shake data-[invalid=true]:bg-destructive/12 data-[invalid=true]:hover:bg-destructive/6 data-[invalid=true]:focus-visible:bg-destructive/6',
        'touch:bg-foreground/2 touch:outline touch:outline-border/6 touch:outline-dashed',
        'placeholder:text-foreground/64 placeholder:transition-opacity focus:placeholder:opacity-0',
        'hide-scrollbar',
        className
      )}
      rows={1}
      {...props}
      value={value}
      onChange={handleChange}
    />
  )
}

const TextEditable = Object.assign(TextEditableRoot, {
  Textarea: TextEditableTextarea,
})

export { TextEditable }
